import styled from 'styled-components';
import { rem } from 'polished';
import { TSizes } from './index';

interface IStyledAvatarSquare {
  disableLink: any;
  theme: any;
  className: any;
  size: TSizes;
}

const stylesBySize = {
  xxs: {
    border: `1px`,
    addition: `2px`,
    gap: `12px`,
    outerRadius: `4px`,
    innerRadius: `2px`,
    pad: `2px`,
    image: 1.66,
  },
  xs: {
    border: `1px`,
    addition: `2px`,
    gap: `16px`,
    outerRadius: `4px`,
    innerRadius: `2px`,
    pad: `3px`,
    image: 2.5,
  },
  sm: {
    border: `1px`,
    addition: `2px`,
    gap: `20px`,
    outerRadius: `6px`,
    innerRadius: `3px`,
    pad: `4px`,
    image: 3.33,
  },
  md: {
    border: `1px`,
    addition: `4px`,
    gap: `24px`,
    outerRadius: `8px`,
    innerRadius: `4px`,
    pad: `5px`,
    image: 5,
  },
  lg: {
    border: `1px`,
    addition: `4px`,
    gap: `28px`,
    outerRadius: `12px`,
    innerRadius: `8px`,
    pad: `6px`,
    image: 6,
  },
  xl: {
    border: `1px`,
    addition: `4px`,
    gap: `36px`,
    outerRadius: `16px`,
    innerRadius: `12px`,
    pad: `6px`,
    image: 7,
  },
  xxl: {
    border: `1px`,
    addition: `4px`,
    gap: `48px`,
    outerRadius: `20px`,
    innerRadius: `16px`,
    pad: `8px`,
    image: 10.5,
  },
};

export const StyledAvatarSquare = styled.div.attrs(
  ({ theme, size }: IStyledAvatarSquare) => {
    const { border, addition, gap, outerRadius, innerRadius, pad, image } =
      stylesBySize[size];
    return {
      imageHeight: theme.rhythmSizing(image),
      imageWidth: theme.rhythmSizing(image),
      borderWidth: border,
      borderAddition: addition,
      borderGap: gap,
      outerRadius,
      innerRadius,
      pad,
    };
  },
)<IStyledAvatarSquare>`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  transform: scale(1);
  transition: transform 0.6s;
  z-index: 999;

  ${({ theme, disableLink }) => {
    if (disableLink) {
      return null;
    }
    return theme.breakpoints.desktop`
      &:hover {
        transform: scale(1.1);
        .avatar__text {
          color: ${theme.colors.pink_2};
        }
        .avatar-square__img {
          transition: filter 300ms ease-in-out;
          filter: brightness(115%);
        }
        .avatar-square__container {
          box-shadow: 0px 0px 12px  rgba(246, 56, 251, 0.5);
          ::after {
            opacity: 1;
            transform: rotate(-45deg) translateY(110%);
          }
        }
        .avatar-square__container,
        .avatar-square__outer-lines {
          transition: border-color 300ms ease-in-out;
          border-color: ${theme.colors.yellow_1};
        }
      }
    `;
  }}
  .avatar-square__outer-lines {
    border-radius: ${({ outerRadius }) => rem(outerRadius)};
    border: ${({ borderWidth, theme }) =>
      `${borderWidth} solid ${theme.colors.pink_2}`};
    padding: ${({ pad }) => rem(pad)};
    background-color: ${({ theme }) => theme.colors.blue_1};
    position: relative;
    &:after,
    &:before {
      content: '';
      z-index: 99;
      position: absolute;
      transition: border-color 300ms ease-in-out, transform 500ms ease;
      background-color: ${({ theme }) => theme.colors.blue_1};
    }
    &:after {
      top: 50%;
      left: -${({ borderWidth }) => borderWidth};
      width: calc(100% + ${({ borderAddition }) => borderAddition});
      height: ${({ borderGap }) => borderGap};
      transform: translateY(-50%);
    }
    &:before {
      top: -${({ borderWidth }) => borderWidth};
      left: 50%;
      width: ${({ borderGap }) => borderGap};
      height: calc(100% + ${({ borderAddition }) => borderAddition});
      transform: translateX(-50%);
    }
  }
  a {
    display: flex;
    flex: 0 1 auto;
    justify-content: center;
  }
  .avatar-square__container {
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
    position: relative;
    overflow: hidden;
    height: ${({ imageHeight }) => imageHeight}rem;
    width: ${({ imageWidth }) => imageWidth}rem;
    border-radius: ${({ innerRadius }) => rem(innerRadius)};
    border: ${({ borderWidth, theme }) =>
      `${borderWidth} solid ${theme.colors.pink_2}`};
    transition: box-shadow 300ms ease-in-out;
    ::after {
      content: '';
      position: absolute;
      top: -100%;
      left: -100%;
      width: 220%;
      height: 220%;
      background: linear-gradient(
        0deg,
        transparent,
        transparent 30%,
        rgba(254, 183, 7, 0.4)
      );
      transform: rotate(-45deg);
      transition: all 0.5s ease;
      opacity: 0;
    }
  }
  .avatar-square__img {
    object-fit: cover;
    height: inherit;
    width: inherit;
    object-position: 50% 50%;
    transition: filter 300ms ease-in-out;
  }
  .avatar-square__text {
    text-align: center;
    line-height: 21px !important;
    margin-left: 12px;
  }
`;
